import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../features/dashai/components/ui/command";
import { DialogTitle } from "../features/dashai/components/ui/dialog";
import { useIsDashmoteStaff } from "../hooks/useIsDashmoteStaff";
import { useAdminStatus } from "../features/dashai/hooks/useAdminStatus";
import { paletteGroups } from "../navigation";
import { foldedCommandFilter } from "../utils/commandFilter";

// A rail button (or anything else) opens the palette by dispatching this event,
// so the trigger stays decoupled from the palette's open state.
export const OPEN_COMMAND_PALETTE_EVENT = "admin:open-command-palette";

const isTypingTarget = (el) => {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
};

/**
 * ⌘K / Ctrl-K command palette over the navigation manifest. Mounted once for
 * authenticated users; it filters every page the user can reach and navigates
 * to the one they pick. Access is still the route guard's job — this only jumps
 * to routes the manifest already exposes to this user.
 */
const AdminCommandPalette = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isStaff = useIsDashmoteStaff();
  const { isAdmin } = useAdminStatus();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k") return;
      // Don't hijack ⌘K while the user types elsewhere, but let it still toggle
      // the palette closed from inside its own input.
      const active = document.activeElement;
      if (isTypingTarget(active) && !active?.closest?.("[cmdk-input-wrapper]")) return;
      event.preventDefault();
      setOpen((prev) => !prev);
    };
    const onOpenEvent = () => setOpen(true);

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener(OPEN_COMMAND_PALETTE_EVENT, onOpenEvent);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, onOpenEvent);
    };
  }, []);

  const goTo = (route) => {
    setOpen(false);
    if (location.pathname !== route) navigate(route);
  };

  const groups = paletteGroups({ isStaff, isAdmin });

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      // Every entry is rendered and the input is uncontrolled, so cmdk's filter
      // is the only thing narrowing the list. Score through the fold so an
      // accented page or section name is reachable from an unaccented query.
      filter={foldedCommandFilter}
    >
      {/* Radix needs an accessible name for the dialog; keep it for screen
          readers without showing a redundant heading. */}
      <DialogTitle className="sr-only">Search pages</DialogTitle>
      <CommandInput placeholder="Search pages…" />
      <CommandList>
        <CommandEmpty>No page found.</CommandEmpty>
        {groups.map((group) => (
          // The pinned entry (Home) has no section name; cmdk can't register a
          // group keyed on null, so give it a stable key and omit the heading.
          <CommandGroup
            key={group.name ?? "__pinned__"}
            heading={group.name ?? undefined}
          >
            {group.entries.map((entry) => (
              <CommandItem
                key={entry.route}
                // cmdk filters on this value, so include the group to let a
                // user find a page by its section too.
                value={`${entry.label} ${group.name ?? ""}`}
                onSelect={() => goTo(entry.route)}
              >
                <entry.icon className="mr-2 h-4 w-4" />
                <span>{entry.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
};

export default AdminCommandPalette;
